import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export type LoanContactScopeSource =
  | 'owner'
  | 'linked_broker'
  | 'loan_book_grant'
  | 'office_admin'
  | 'company_admin';

interface LoanContactCapabilityResponse {
  canLogActivity: boolean;
  scopeSource: LoanContactScopeSource | null;
}

export function useLoanContactCapability(companyId: string, loanId: string) {
  const [capability, setCapability] = useState<LoanContactCapabilityResponse | null>(null);
  const [resolvedKey, setResolvedKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const capabilityKey = `${companyId}\u0000${loanId}`;

  useEffect(() => {
    let current = true;
    setCapability(null);
    setResolvedKey(null);
    if (!companyId || !loanId) {
      setCapability({ canLogActivity: false, scopeSource: null });
      setResolvedKey(capabilityKey);
      setLoading(false);
      return () => {
        current = false;
      };
    }
    setLoading(true);

    const load = async () => {
      try {
        const fn = httpsCallable<
          { companyId: string; loanId: string },
          LoanContactCapabilityResponse
        >(functions, 'getLoanContactCapability');
        const result = await fn({ companyId, loanId });
        if (current) {
          setCapability(result.data);
          setResolvedKey(capabilityKey);
        }
      } catch (error) {
        // Capabilities fail closed. The mutation callable independently enforces
        // the same policy, so a transient read error cannot expose a write.
        console.error('[useLoanContactCapability] failed:', error);
        if (current) {
          setCapability({ canLogActivity: false, scopeSource: null });
          setResolvedKey(capabilityKey);
        }
      } finally {
        if (current) setLoading(false);
      }
    };

    void load();
    return () => {
      current = false;
    };
  }, [capabilityKey, companyId, loanId]);

  const isCurrent = resolvedKey === capabilityKey;
  return {
    canLogActivity: isCurrent && capability?.canLogActivity === true,
    scopeSource: isCurrent ? capability?.scopeSource ?? null : null,
    loading: !isCurrent || loading,
  };
}
